import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  LuWrench,
  LuStar,
  LuCopy,
  LuCopyCheck,
  LuLoader,
} from "react-icons/lu";
import type {
  SourceSnapshot,
  UserFavorite,
  UserRoom,
} from "@tuit/shared-types";
import { GROUPS } from "../groups.js";
import { ViewSettings } from "./ViewSettings.js";
import { AboutPanel } from "./AboutPanel.js";
import DoorOpen from "./common/Icons.js";
import { getFaviconUrl } from "../utils/favicon.js";
import styles from "../styles/components/Sidebar.module.css";

interface Props {
  sections: SourceSnapshot[];
  readIds: Set<string>;
  suppressed: Set<string>;
  activeSectionId: string | null;
  selectedGroupId: string | null;
  favorites: UserFavorite[];
  isSignedIn: boolean;
  isAdmin: boolean;
  userRooms: UserRoom[];
  searchQuery: string;
  onGroupChange: (groupId: string | null) => void;
  onSwap: (srcId: string, dropId: string) => void;
  onFavoriteToggle: (
    roomId: string,
    roomType: "system" | "user",
    isFavorited: boolean,
  ) => Promise<void>;
  onSearch: (query: string) => void;
  onSearchClear: () => void;
  matchCount: number | null;
  isOpen: boolean;
  onClose: () => void;
  isHomeRoom: boolean;
}

function scrollTo(id: string) {
  const el = document.getElementById(`section-${id}`);
  if (!el) return;
  const header = document.querySelector<HTMLElement>("[data-header]");
  const offset = header ? header.offsetHeight + 8 : 52;
  const scroller = document.querySelector<HTMLElement>(
    "[data-content-scroller]",
  );
  const rect = el.getBoundingClientRect();

  if (scroller) {
    scroller.scrollTo({ top: el.offsetTop - offset, behavior: "smooth" });
  } else {
    window.scrollTo({
      top: rect.top + window.scrollY - offset,
      behavior: "smooth",
    });
  }
}

export function Sidebar({
  sections,
  readIds,
  suppressed,
  activeSectionId,
  selectedGroupId,
  favorites,
  isSignedIn,
  isAdmin,
  userRooms,
  searchQuery,
  onGroupChange,
  onSwap,
  onFavoriteToggle,
  onSearch,
  onSearchClear,
  matchCount,
  isOpen,
  onClose,
  isHomeRoom,
}: Props) {
  const [roomPanelOpen, setRoomPanelOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [myRoomsOpen, setMyRoomsOpen] = useState(true);
  const [dragSrcId, setDragSrcId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [favPending, setFavPending] = useState(false);
  const favoritedIds = new Set(favorites.map((f) => f.roomId));

  const isCurrentFav = selectedGroupId
    ? favoritedIds.has(selectedGroupId)
    : false;
  const currentRoomType: "system" | "user" = GROUPS.some(
    (g) => g.id === selectedGroupId,
  )
    ? "system"
    : "user";

  function handleCopyUrl(e: React.MouseEvent) {
    const url = selectedGroupId
      ? `${window.location.origin}/~${selectedGroupId}`
      : window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    e.stopPropagation();
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <nav
      className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className={styles.searchBar}
        onSubmit={(e) => {
          e.preventDefault();
          if (localQuery.trim()) onSearch(localQuery.trim());
        }}
      >
        <div className={styles.searchGroup}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search posts…"
            value={localQuery}
            onChange={(e) => {
              setLocalQuery(e.target.value);
              if (e.target.value) onSearch(e.target.value);
              else onSearchClear();
            }}
          />
          {localQuery ? (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => {
                setLocalQuery("");
                onSearchClear();
              }}
            >
              ×
            </button>
          ) : (
            <button type="submit" className={styles.searchSubmit}>
              ⌕
            </button>
          )}
        </div>
      </form>
      {matchCount !== null && (
        <div className={styles.matchCount}>
          {matchCount} {matchCount === 1 ? "match" : "matches"}
        </div>
      )}

      <div className={styles.roomToggleRow}>
        <button
          type="button"
          className={styles.roomToggle}
          onClick={() => setRoomPanelOpen((v) => !v)}
        >
          <span className={styles.roomToggleTitle}>
            <DoorOpen width={25} height={25} color="var(--color-primary)" />
            <span className={styles.roomToggleTitleText}>
              {isHomeRoom
                ? "Home"
                : (GROUPS.find((g) => g.id === selectedGroupId)?.title ??
                  userRooms.find((r) => r.id === selectedGroupId)?.title ??
                  "Room")}
              {!isHomeRoom && selectedGroupId && (
                <span className={styles.roomToggleSlug}>
                  /~{selectedGroupId}
                </span>
              )}
            </span>
          </span>
          <span
            className={`${styles.settingsChevron} ${roomPanelOpen ? styles.settingsChevronOpen : ""}`}
          >
            ▾
          </span>
        </button>

        {selectedGroupId && (
          <div className={styles.roomToggleActions}>
            {isSignedIn && !isHomeRoom && (
              <button
                type="button"
                className={styles.roomToggleActionBtn}
                title={
                  isCurrentFav ? "Remove from favorites" : "Add to favorites"
                }
                disabled={favPending}
                onClick={async (e) => {
                  e.stopPropagation();
                  setFavPending(true);
                  try {
                    await onFavoriteToggle(
                      selectedGroupId,
                      currentRoomType,
                      isCurrentFav,
                    );
                  } finally {
                    setFavPending(false);
                  }
                }}
              >
                {favPending ? (
                  <LuLoader size={14} className={styles.spinning} />
                ) : (
                  <LuStar size={14} fill={isCurrentFav ? "currentColor" : "none"} />
                )}
              </button>
            )}
            <button
              type="button"
              className={styles.roomToggleActionBtn}
              title={copied ? "Copied!" : "Copy URL"}
              onClick={handleCopyUrl}
            >
              {copied ? <LuCopyCheck size={14} /> : <LuCopy size={14} />}
            </button>
          </div>
        )}
      </div>
      <div
        className={`${styles.settingsPanel} ${styles.roomPanel} ${roomPanelOpen ? styles.settingsPanelOpen : ""}`}
      >
        <div className={styles.settingsPanelInner}>
          <div className={styles.roomPanelScroll}>
            <div className={styles.roomList}>
              {GROUPS.map((g) => {
                const isFav = favoritedIds.has(g.id);
                return (
                  <div key={g.id} className={styles.roomRow}>
                    <button
                      type="button"
                      className={`${styles.roomItem} ${g.id === selectedGroupId ? styles.roomItemActive : ""}`}
                      onClick={() => {
                        onGroupChange(g.id);
                        setRoomPanelOpen(false);
                      }}
                    >
                      <DoorOpen
                        width={16}
                        height={16}
                        color="var(--color-secondary)"
                      />
                      {g.title}
                    </button>
                    {isSignedIn && (
                      <button
                        type="button"
                        className={`${styles.starBtn} ${isFav ? styles.starBtnActive : ""}`}
                        title={
                          isFav ? "Remove from favorites" : "Add to favorites"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onFavoriteToggle(g.id, "system", isFav);
                        }}
                      >
                        {isFav ? "★" : "☆"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {isSignedIn && (
              <div className={styles.myRoomsLink}>
                <Link
                  to="/my-rooms"
                  className={styles.myRoomsBtn}
                  onClick={() => setRoomPanelOpen(false)}
                >
                  My Rooms →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {favorites.length > 0 && (
        <div
          className={`${styles.favoritesSection} ${roomPanelOpen ? styles.hidden : ""}`}
        >
          <div className={styles.groupPickerLabel}>Favorites</div>
          {favorites.map((fav) => (
            <div key={fav.roomId} className={styles.itemWrapper}>
              <button
                type="button"
                className={`${styles.item} ${selectedGroupId === fav.roomId ? styles.active : ""}`}
                onClick={() => {
                  if (fav.roomType === "system") {
                    onGroupChange(fav.roomId);
                  }
                }}
              >
                {fav.roomType === "user" ? (
                  <Link
                    to="/$roomSlug"
                    params={{ roomSlug: `~${fav.roomId}` }}
                    className={styles.favLink}
                  >
                    {fav.title}
                  </Link>
                ) : (
                  <span className={styles.name}>{fav.title}</span>
                )}
              </button>
              <button
                type="button"
                className={`${styles.starBtn} ${styles.starBtnActive}`}
                title="Remove from favorites"
                onClick={() => onFavoriteToggle(fav.roomId, fav.roomType, true)}
              >
                ★
              </button>
            </div>
          ))}
        </div>
      )}

      {isSignedIn && userRooms.length > 0 && (
        <div
          className={`${styles.myRoomsSection} ${roomPanelOpen ? styles.hidden : ""}`}
        >
          <button
            type="button"
            className={styles.myRoomsSectionToggle}
            onClick={() => setMyRoomsOpen((v) => !v)}
          >
            <span>My Rooms</span>
            <span
              className={`${styles.settingsChevron} ${myRoomsOpen ? styles.settingsChevronOpen : ""}`}
            >
              ▾
            </span>
          </button>
          <div
            className={`${styles.myRoomsCollapse} ${myRoomsOpen ? styles.myRoomsCollapseOpen : ""}`}
          >
            <div className={styles.myRoomsCollapseInner}>
              {userRooms.map((room) => (
                <div key={room.id} className={styles.itemWrapper}>
                  <Link
                    to="/$roomSlug"
                    params={{ roomSlug: `~${room.id}` }}
                    className={`${styles.myRoomLink} ${selectedGroupId === room.id ? styles.active : ""}`}
                  >
                    <span className={styles.name}>{room.title}</span>
                  </Link>
                  <Link
                    to="/my-rooms/edit/$roomId"
                    params={{ roomId: room.id }}
                    className={styles.myRoomEditBtn}
                    title="Edit room"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LuWrench size={13} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        className={`${styles.scrollContent} ${roomPanelOpen ? styles.hidden : ""}`}
      >
        <div className={styles.group}>
          {sections.map((section) => {
            const unread = section.items.filter(
              (item) => !suppressed.has(item.id) && !readIds.has(item.id),
            ).length;
            const isActive = activeSectionId === section.sourceId;
            const isEmpty = unread === 0;
            const isDragging = dragSrcId === section.sourceId;
            const isOver = dragOverId === section.sourceId && !isDragging;

            return (
              <div
                key={section.sourceId}
                className={`${styles.itemWrapper} ${isDragging ? styles.itemWrapperDragging : ""} ${isOver ? styles.itemWrapperDragOver : ""}`}
                draggable
                onDragStart={(e) => {
                  setDragSrcId(section.sourceId);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  setDragSrcId(null);
                  setDragOverId(null);
                }}
                onDragEnter={() => setDragOverId(section.sourceId)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragSrcId && dragSrcId !== section.sourceId)
                    onSwap(dragSrcId, section.sourceId);
                  setDragSrcId(null);
                  setDragOverId(null);
                }}
              >
                <button
                  type="button"
                  className={`${styles.item} ${isActive ? styles.active : ""} ${isEmpty ? styles.empty : ""}`}
                  onClick={() => scrollTo(section.sourceId)}
                  title={section.title}
                >
                  <span className={styles.dragHandle} />
                  {(section.faviconUrl || section.items[0]?.domain) && (
                    <span
                      className={styles.faviconWrap}
                      style={section.iconBackground ? { background: section.iconBackground } : undefined}
                    >
                      <img
                        src={section.faviconUrl ?? getFaviconUrl(section.items[0]?.domain ?? "")}
                        className={styles.favicon}
                        width={14}
                        height={14}
                        alt=""
                        aria-hidden="true"
                      />
                    </span>
                  )}
                  <span className={styles.name}>{section.title}</span>
                  {section.fetchStatus !== "never" && (
                    <span
                      className={`${styles.statusPip} ${section.fetchStatus === "ok" ? styles.statusOk : styles.statusError}`}
                      title={
                        section.fetchStatus === "error"
                          ? (section.errorMessage ?? "Feed error")
                          : "Feed OK"
                      }
                    />
                  )}
                  {unread > 0 && (
                    <span className={styles.badge}>{unread}</span>
                  )}
                </button>
                {isAdmin && (
                  <a
                    href={`/source/${section.sourceId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.editFeedBtn}
                    title={`Edit ${section.title}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LuWrench size={12} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.settingsSection}>
        <button
          type="button"
          className={styles.settingsToggle}
          onClick={() => setSettingsOpen((v) => !v)}
        >
          <span>Settings</span>
          <span
            className={`${styles.settingsChevron} ${settingsOpen ? styles.settingsChevronOpen : ""}`}
          >
            ▾
          </span>
        </button>
        <div
          className={`${styles.settingsPanel} ${settingsOpen ? styles.settingsPanelOpen : ""}`}
        >
          <div className={styles.settingsPanelInner}>
            <ViewSettings />
          </div>
        </div>
      </div>
      <div className={styles.settingsSection}>
        <button
          type="button"
          className={styles.settingsToggle}
          onClick={() => setAboutOpen((v) => !v)}
        >
          <span>About</span>
          <span
            className={`${styles.settingsChevron} ${aboutOpen ? styles.settingsChevronOpen : ""}`}
          >
            ▾
          </span>
        </button>
        <div
          className={`${styles.settingsPanel} ${aboutOpen ? styles.settingsPanelOpen : ""}`}
        >
          <div className={styles.settingsPanelInner}>
            <AboutPanel />
          </div>
        </div>
      </div>
    </nav>
  );
}
